import React, { memo, ReactNode, useCallback, useContext } from 'react'
import tw from 'twin.macro'
import { Tooltip } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button } from 'src/components/shared/Button'
import { SideNavContext } from './SideNav.context'
import { sideNavEntryStyles } from './sideNavEntryStyles'

interface Props {
  to: string
  label: string
  icon: ReactNode
  disabled?: boolean
  /**
   * Requires an exact pathname match instead of the default prefix match. Needed for
   * nav items (e.g. a customer's Overview) whose path is itself a prefix of sibling
   * routes (its tabs), which would otherwise also render active.
   */
  matchExact?: boolean
  dataTestId?: string
  /** Trailing text in the expanded rail, such as a count. Hidden while collapsed. */
  badge?: string
}

/**
 * A rail entry in the dashboard `SideMenu` shape: a full-width `small` Button, grey at rest and
 * white when it is the current route. Collapsed, the same button shrinks to a centred 36px
 * square and the label moves into a tooltip — see `sideNavEntryStyles` for why the icon does not
 * move while that happens.
 */
const SideNavItemComponent = ({
  to,
  label,
  icon,
  disabled,
  matchExact,
  dataTestId,
  badge,
}: Props) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { isCollapsed } = useContext(SideNavContext)

  // Prefix match so a deep route (e.g. the flow editor under a customer's
  // onboarding-templates tab) keeps its parent nav item highlighted.
  const isActive = matchExact
    ? location.pathname === to
    : location.pathname === to || location.pathname.startsWith(`${to}/`)

  const handleClick = useCallback(() => {
    navigate(to)
  }, [navigate, to])

  return (
    <Tooltip title={isCollapsed ? label : ''} placement="right">
      {/* Tooltip needs a hoverable child even when the button is disabled. */}
      <span css={tw`relative mb-1 flex`}>
        <Button
          size="small"
          color={isActive ? 'white' : 'grey'}
          disabled={disabled}
          className={sideNavEntryStyles(isCollapsed, isActive)}
          leftIcon={
            <span css={tw`flex h-5 w-5 items-center justify-center text-shades-1000`}>{icon}</span>
          }
          aria-label={isCollapsed ? label : undefined}
          aria-current={isActive ? 'page' : undefined}
          onClick={handleClick}
          {...(dataTestId ? { 'data-testid': dataTestId } : {})}
        >
          {isCollapsed ? undefined : label}
        </Button>
        {/* Overlaid rather than passed as Button children: Button takes a string label only, and its
            label is not a flex-1 item, so a badge placed beside it could not sit at the right edge. */}
        {badge !== undefined && !isCollapsed ? (
          <span
            css={tw`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-shades-500`}
          >
            {badge}
          </span>
        ) : null}
      </span>
    </Tooltip>
  )
}

export const SideNavItem = memo(SideNavItemComponent)
